'use client';

import { useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent } from 'react';

import { Spinner } from './spinner';
import type { ImageZoomApi } from './use-image-zoom';
import { cn } from '../utils/cn';
import type { LightboxAttachment } from './attachment-lightbox';

interface AttachmentLightboxImageProps {
	attachment: LightboxAttachment;
	/** The single shared zoom instance - its ref is bound only on the active slide. */
	zoom: ImageZoomApi;
	isActive: boolean;
	onContextMenu: (e: ReactMouseEvent) => void;
}

/** Which url settled, and how. Anything else (a new url included) is still loading. */
interface SettledImage {
	url: string;
	status: 'loaded' | 'error';
}

const MEDIA_BOX = 'max-h-[85dvh] max-w-[90vw] select-none rounded-lg';
const STACKED = 'col-start-1 row-start-1';

/**
 * The lightbox image. Originals run 3-15 MB, so until the file lands the
 * already-cached chat thumbnail stands in for it, dimmed and pulsing under a
 * spinner, and the full image stays mounted but invisible.
 */
export function AttachmentLightboxImage({
	attachment,
	zoom,
	isActive,
	onContextMenu,
}: AttachmentLightboxImageProps) {
	const { url, thumbnailUrl, width, height, fileName } = attachment;
	const [settled, setSettled] = useState<SettledImage | null>(null);
	const status = settled?.url === url ? settled.status : 'loading';
	// Inactive slides get their own ref: writing to the shared zoom ref from a
	// neighbor would null out the active slide's element.
	const inactiveRef = useRef<HTMLImageElement | null>(null);
	const imageRef = isActive ? zoom.ref : inactiveRef;

	// A cached original is complete before its load event could be observed.
	// Settle it before paint so the placeholder never flashes.
	useLayoutEffect(() => {
		const img = imageRef.current;
		if (img && img.complete && img.naturalWidth > 0) setSettled({ url, status: 'loaded' });
	}, [url, imageRef]);

	const showPlaceholder = !!thumbnailUrl && status !== 'loaded';

	// The thumbnail is ~1024px at most, so at its natural size it would be
	// smaller than the original on a large screen and the picture would jump at
	// the swap. Size both layers to the box the original will occupy.
	const hasDimensions = !!width && !!height && width > 0 && height > 0;
	const boxStyle: CSSProperties | undefined = hasDimensions
		? {
				width: `min(90vw, calc(85dvh * (${width} / ${height})), ${width}px)`,
				aspectRatio: `${width} / ${height}`,
				height: 'auto',
			}
		: undefined;

	// Zoom only drives the active slide; neighbors render static.
	const zoomStyle: CSSProperties | undefined = isActive
		? {
				transform: `translate3d(${zoom.transform.x}px, ${zoom.transform.y}px, 0) scale(${zoom.transform.scale})`,
				transition: zoom.isAnimating ? 'transform 180ms ease-out' : 'none',
				willChange: 'transform',
			}
		: undefined;

	return (
		// Shrinks to the image box, so a click on the empty area around it still
		// lands on the slide and closes the lightbox.
		<div className="relative grid place-items-center">
			{showPlaceholder && (
				// The dim sits on the wrapper because the pulse animates opacity
				// itself: on one element it would replace the dim, not multiply it.
				<div
					className={cn(
						STACKED,
						'pointer-events-none',
						status === 'loading' && 'opacity-60',
					)}
				>
					<img
						src={thumbnailUrl}
						alt=""
						aria-hidden="true"
						draggable={false}
						className={cn(
							MEDIA_BOX,
							// Stored dimensions can disagree with the served bytes; letterbox
							// rather than stretch when they do.
							'object-contain',
							status === 'loading' && 'animate-pulse motion-reduce:animate-none',
						)}
						style={{ ...boxStyle, ...zoomStyle }}
					/>
				</div>
			)}
			<img
				// A new url gets a new element: `complete` on a reused one still
				// describes the previous file for a moment after src changes.
				key={url}
				ref={imageRef}
				src={url}
				alt={fileName}
				draggable={false}
				onClick={isActive ? zoom.onClick : undefined}
				onContextMenu={onContextMenu}
				onDragStart={(e) => e.preventDefault()}
				onLoad={(e) => {
					// Downloaded is not yet paintable: a large original takes a few
					// frames to decode, and dropping the thumbnail before that would
					// blink the backdrop through. A failed decode still settles.
					const settle = () => setSettled({ url, status: 'loaded' });
					const img = e.currentTarget;
					if (typeof img.decode === 'function') void img.decode().then(settle, settle);
					else settle();
				}}
				onError={() => setSettled({ url, status: 'error' })}
				className={cn(
					STACKED,
					MEDIA_BOX,
					// Hidden behind the thumbnail so a half-downloaded file never shows
					// its top-down partial paint.
					showPlaceholder && 'opacity-0',
					// Let a swipe that starts on the image pan the carousel - unless the
					// image is zoomed, where touch is captured for panning the image.
					isActive && zoom.isZoomed ? 'touch-none' : 'touch-pan-x',
					isActive && (zoom.isZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'),
				)}
				// Explicit size only while the thumbnail stands in: once loaded the
				// image sizes itself from its own bytes, exactly as before.
				style={{ ...(showPlaceholder ? boxStyle : undefined), ...zoomStyle }}
			/>
			{status === 'loading' && (
				// Outside the zoom transform, so it stays centered while the image pans.
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
					<Spinner className="size-6 text-neutral/70" />
				</div>
			)}
		</div>
	);
}
